import { forwardRef, useEffect, useRef, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import Calendar from "@/components/ui/calendar";
import {
  Calendar as CalendarIcon,
  Clock,
  DueDate,
  Close
} from "@dreamhub/designsystem/icons";
import { Calendar as LucideCalendar } from "lucide-react";
import { IconButton } from "@/components/ui/icon-button";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";
import { cn } from "@/utils/utils";
import {
  formatFullDate,
  isWithinDays,
  parseTimeString,
  simpleDate,
  toLocalDate,
  toDateOnlyISO
} from "@/utils/datetime";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { isSameDay } from "date-fns";
import { useTranslation } from "react-i18next";

export type dateTimeVariant = "form" | "badge";

export interface DateTimeFieldProps {
  type: "date" | "time";
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  nearestTime?: string; // Format: "12:15 AM"
  className?: string;
  iconPosition?: "left" | "right";
  variant?: dateTimeVariant;
  isDueDate?: boolean;
  showDatePresets?: boolean;
  availableFrom?: string;
  availableTo?: string;
  // Read-only presentation: the field still shows its value but opens no picker.
  disabled?: boolean;
  // Withholds the clear control unless `clearable` is set. The clear button
  // is built inside this component rather than supplied by the caller, so
  // unlike every sibling control — which gates on `!required ? onClear :
  // undefined` at the call site — a caller has no other way to keep a
  // required date from being emptied.
  required?: boolean;
  // Offers clear on a required date; the form's validation still blocks submit.
  clearable?: boolean;
  // Appends "(Today)" when the selected date is today.
  showTodayLabel?: boolean;
}

const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const hour12 = hour % 12 || 12;
      const ampm = hour < 12 ? "AM" : "PM";
      const timeString = `${hour12}:${minute.toString().padStart(2, "0")} ${ampm}`;
      slots.push(timeString);
    }
  }
  return slots;
};

const findNearestSlot = (nearestTime?: string) => {
  if (nearestTime) {
    const parsed = parseTimeString(nearestTime);
    if (parsed) {
      return parsed;
    }
  }

  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  // Round up to nearest 15 minutes
  const roundedMinute = Math.ceil(currentMinute / 15) * 15;

  // Handle minute rollover to next hour
  if (roundedMinute === 60) {
    return {
      hour: (currentHour + 1) % 24,
      minute: 0
    };
  }

  return {
    hour: currentHour,
    minute: roundedMinute
  };
};

const DateTimeField = forwardRef<HTMLInputElement, DateTimeFieldProps>(
  (
    {
      type,
      value,
      onChange,
      placeholder,
      isOpen,
      onOpenChange,
      name,
      nearestTime,
      className,
      iconPosition = "right",
      variant,
      isDueDate,
      showDatePresets = false,
      availableFrom,
      availableTo,
      disabled = false,
      required = false,
      clearable = false,
      showTodayLabel = false
    },
    ref
  ) => {
    const { t } = useTranslation();
    const isNewFieldsUI = useNewFieldsUI();
    const timeSlots = type === "time" ? generateTimeSlots() : [];
    const [mounted, setMounted] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);
    const nearestRef = useRef(findNearestSlot());
    const triggerRef = useRef<HTMLDivElement>(null);

    const hasBadgeLook = variant === "badge";

    const isTodaySelected = isSameDay(toLocalDate(value), new Date());

    useEffect(() => {
      nearestRef.current = findNearestSlot(nearestTime);
    }, [nearestTime]);

    useEffect(() => {
      if (type === "time" && isOpen) {
        setMounted(true);
      }
    }, [isOpen, type]);

    useEffect(() => {
      if (mounted && listRef.current) {
        const nearest = nearestRef.current;
        const targetIndex = nearest.hour * 4 + Math.floor(nearest.minute / 15);
        const targetElement = listRef.current.children[
          targetIndex
        ] as HTMLElement;

        if (targetElement) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const container = listRef.current;
              if (!container) return;

              const ROW_HEIGHT = 32;
              const OFFSET_ROWS = 2;

              // Calculate the scroll position
              const scrollPosition = Math.max(
                0,
                (targetIndex - OFFSET_ROWS) * ROW_HEIGHT
              );

              container.scrollTop = scrollPosition;
            });
          });
        }
        setMounted(false);
      }
    }, [mounted]);

    const handlePresetSelect = (date: Date) => {
      onChange(toDateOnlyISO(date));
      onOpenChange(false);
    };

    const dueDateTriggerFormat = () => {
      if (isTodaySelected) {
        return t("dateTime.presets.today");
      }
      if (isWithinDays(toLocalDate(value), new Date(), 1)) {
        return t("dateTime.presets.tomorrow");
      }
      return simpleDate(toLocalDate(value), "short", true, true);
    };

    // Both renderings share this shell; the flag only changes the icon slot.
    // `trailingIcon` is a separate right-edge slot so a left-icon field (due
    // date) keeps its calendar icon while the clear "x" sits on the right. With
    // one present, the icon+text pair moves into an inner group so the trailing
    // icon does not inherit the row's icon-to-text gap; without one, the flat
    // markup is unchanged.
    const renderTriggerShell = (
      iconContainer: JSX.Element,
      trailingIcon?: JSX.Element
    ) => {
      const formattedValue =
        type === "date" && value
          ? isDueDate
            ? dueDateTriggerFormat()
            : formatFullDate(value, true, false)
          : value;
      const displayValue =
        formattedValue && showTodayLabel && isTodaySelected
          ? `${formattedValue} (${t("dateTime.presets.today")})`
          : formattedValue;

      const textNode = (
        <Text
          as="span"
          variant={displayValue ? "body" : "bodyTertiary"}
          className={cn(
            "leading-none",
            isDueDate && isTodaySelected ? "!text-warning" : ""
          )}
        >
          {displayValue || placeholder}
        </Text>
      );

      return (
        <div
          ref={triggerRef}
          className={cn(
            "relative rounded-lg",
            isOpen ? "shadow-field-focus" : "shadow-field",
            disabled && "opacity-50"
          )}
        >
          <div
            ref={ref}
            className={cn(
              "w-full h-9 border-[0.5px] border-border-field-default rounded-lg pl-3 pr-2 py-[6px] flex items-center cursor-pointer transition-all duration-200 ease-in-out bg-surface-field-default",
              disabled && "cursor-not-allowed",
              isOpen && "border-border-field-focus bg-surface-focus-selected",
              iconPosition === "right" || trailingIcon
                ? "justify-between"
                : "gap-3",
              hasBadgeLook &&
                "p-2 gap-[6px] rounded-lg !border-[0.5px] !border-border-border3 bg-badges-default",
              isOpen &&
                hasBadgeLook &&
                "!bg-surface-focus-selected !border-border-field-focus",
              className
            )}
            data-test={`${name}-input`}
          >
            {iconPosition === "left" ? (
              trailingIcon ? (
                <div
                  className={cn(
                    "flex items-center min-w-0",
                    hasBadgeLook ? "gap-[6px]" : "gap-3"
                  )}
                >
                  {iconContainer}
                  {textNode}
                </div>
              ) : (
                <>
                  {iconContainer}
                  {textNode}
                </>
              )
            ) : (
              textNode
            )}
            {iconPosition === "right" && !trailingIcon && iconContainer}
            {trailingIcon}
          </div>
        </div>
      );
    };

    const Icon = isDueDate ? DueDate : type === "date" ? CalendarIcon : Clock;

    const renderLegacyTrigger = () =>
      renderTriggerShell(
        <IconContainer
          color={isDueDate && isTodaySelected ? "warning" : "secondary"}
          className="flex-shrink-0"
        >
          <Icon />
        </IconContainer>
      );

    const mainIcon = (
      <IconContainer
        color={isDueDate && isTodaySelected ? "warning" : "secondary"}
        size="5"
        className="flex-shrink-0"
      >
        {type === "date" && !isDueDate ? (
          <LucideCalendar size={20} strokeWidth={1.5} />
        ) : (
          <Icon />
        )}
      </IconContainer>
    );

    // Icon-button treatment (hover/press background, tooltip) matching the
    // priority field's clear control. `visibilityClassName` drives the
    // opacity/pointer-events/width transition for the always-mounted,
    // left-positioned case below — omitted for the right-positioned case,
    // which stays fully visible whenever it's swapped in.
    const renderClearButton = (
      visibilityClassName?: string,
      isHidden = false
    ) => (
      <Tooltip>
        <TooltipTrigger asChild>
          <IconButton
            size="xs"
            variant="iconNoBg"
            type="button"
            aria-label={t("common.clear")}
            iconClassName="[&_svg]:size-4"
            // Stays mounted so width and opacity can transition, so while it is
            // invisible it has to leave the tab order too — pointer-events-none
            // only stops the mouse.
            tabIndex={isHidden ? -1 : undefined}
            aria-hidden={isHidden || undefined}
            className={cn(
              "transition-all duration-200 ease-in-out",
              visibilityClassName
            )}
            onClick={(clickEvent) => {
              clickEvent.stopPropagation();
              onChange("");
              onOpenChange(false);
            }}
            onMouseDown={(mouseEvent) => mouseEvent.preventDefault()}
          >
            <Close />
          </IconButton>
        </TooltipTrigger>
        <TooltipContent>{t("common.clear")}</TooltipContent>
      </Tooltip>
    );

    // A right-positioned icon gives its slot over to the clear button while the
    // picker is open; a left-positioned one (due date) keeps its icon and the
    // clear button uses the shell's trailing slot. Always mounted on a date
    // field so opacity/width can transition rather than popping in and out.
    const renderNewTrigger = () => {
      // `disabled` can flip while a controlled `isOpen` stays true, and the
      // clear IconButton takes no disabled prop of its own.
      const showClear =
        !disabled &&
        (!required || clearable) &&
        Boolean(isOpen && value && type === "date");
      if (iconPosition === "left") {
        return renderTriggerShell(
          mainIcon,
          type === "date"
            ? renderClearButton(
                showClear
                  ? "opacity-100 pointer-events-auto"
                  : "opacity-0 w-0 pointer-events-none",
                !showClear
              )
            : undefined
        );
      }
      // pointer-events-auto is not decoration: the popover is modal, so Radix
      // sets pointer-events: none on body and the trigger shell inherits it.
      // Without this the hit test skips the button, Radix reads the pointerdown
      // as outside and closes the popover, and onChange("") never runs — the
      // control looks right and does nothing. Default iconPosition is "right",
      // so this is the path every Form date field takes.
      return renderTriggerShell(
        showClear ? renderClearButton("pointer-events-auto") : mainIcon
      );
    };

    const renderTrigger = () =>
      isNewFieldsUI ? renderNewTrigger() : renderLegacyTrigger();

    const renderTimeContent = () => (
      <div ref={listRef} className="h-64 overflow-y-auto overscroll-contain">
        {timeSlots.map((time) => {
          const isCurrent = value === time;
          return (
            <div
              key={time}
              className={cn(
                "w-full flex px-2 py-1.5 text-base rounded-lg cursor-pointer transition-colors",
                isCurrent
                  ? "!text-primary cursor-not-allowed !bg-disabled hover:!bg-disabled"
                  : "hover:!bg-surface-focus-selected text-secondary"
              )}
              onClick={() => {
                onChange(time);
                onOpenChange(false);
              }}
            >
              {time}
            </div>
          );
        })}
      </div>
    );

    const renderDateContent = () => (
      <Calendar
        mode="single"
        selected={value ? toLocalDate(value) : undefined}
        onSelect={(date) => {
          if (date) {
            onChange(toDateOnlyISO(date));
          }
          onOpenChange(false);
        }}
        showPresets={showDatePresets}
        presetLabels={{
          today: t("dateTime.presets.today"),
          tomorrow: t("dateTime.presets.tomorrow"),
          endOfWeek: t("dateTime.presets.endOfWeek"),
          nextWeek: t("dateTime.presets.nextWeek"),
          inTwoWeeks: t("dateTime.presets.inTwoWeeks"),
          inAMonth: t("dateTime.presets.inAMonth")
        }}
        onPresetSelect={handlePresetSelect}
        availableFrom={availableFrom ? toLocalDate(availableFrom) : undefined}
        availableTo={availableTo ? toLocalDate(availableTo) : undefined}
      />
    );

    const popoverContentClass =
      type === "date"
        ? cn("w-fit", showDatePresets && "w-auto")
        : "flex flex-col gap-2 pt-1 w-[--radix-popper-anchor-width] relative";

    return (
      <Popover
        modal
        open={disabled ? false : isOpen}
        onOpenChange={(open) => !disabled && onOpenChange(open)}
      >
        <PopoverTrigger asChild>{renderTrigger()}</PopoverTrigger>
        <PopoverContent
          padding={type === "date" ? 0 : 4}
          className={popoverContentClass}
          align="start"
          sideOffset={4}
          // In modal mode Radix treats a pointerdown on the trigger as outside
          // and closes first, collapsing the clear button (only visible while
          // open) before its click fires. Excluding the trigger is what Radix's
          // own non-modal path does; same fix as SingleSelect's clear button.
          onPointerDownOutside={(e) => {
            if (triggerRef.current?.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
        >
          {type === "date" ? renderDateContent() : renderTimeContent()}
        </PopoverContent>
      </Popover>
    );
  }
);
DateTimeField.displayName = "DateTimeField";

export default DateTimeField;
